import * as React from 'react'

import {
  Media,
  MuteButton,
  PauseButton,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'
import { RightArrow, RotateClockwise, SelectMark } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import {
  imageZoomPortalClassName,
  modalAnimationDuration,
  standardModalBodyOpenClassName,
} from '../../../../../brunswick/styles/constants'
import { trackEvent } from '../../../../../lib/analytics'
import { resizeShopifyImageSource } from '../../../../../lib/utils/images'
import { ProductAsset } from '../../../../../types/graphql'
import { NextModal } from '../../../../modals/NextModal'
import { GalleryImageStateContainer } from '../contexts/GalleryImageStateContainer'
import { ExtendedPinchZoom } from '../contexts/GalleryImageStateContainer/index'
import { GalleryImageCarousel } from '../GalleryImageCarousel'
import { GalleryImagePaginationText } from '../GalleryImagePaginationText'

import PinchZoom from './PinchZoom'
import {
  CaretLeft,
  CaretRight,
  CloseButton,
  Container,
  LeftArrowWrap,
  PinchZoomImage,
  PinchZoomImageWrapper,
  RightArrowWrap,
  StyledVideoButtonsWrapper,
  Video,
  modalStyles,
} from './styles'

const imageSize = 1200
const zoomedImageSize = imageSize * 2
const edgeSwipeThreshold = 100
const edgeTouchThreshold = 10
const maxIntervalBetweenZoomEvents = 300

export type ZoomTouchArea = 'left' | 'middle' | 'right' | null

export const ImageZoom: React.FC = () => {
  const {
    allAssets,
    currentAssetIndex,
    hideImageZoom,
    isImageZoomShown,
    isZoomed,
    pinchZoomNodes,
    revealNextSlide,
    revealPreviousSlide,
    setIsZoomed,
    setPinchZoomNode,
  } = GalleryImageStateContainer.useContainer()

  const {
    pages: {
      product: { gallery: strings },
    },
  } = useTranslation()

  const isSmallViewport = useIsViewport(ViewportSizes.small)
  const [isMouseDown, setIsMouseDown] = React.useState(false)

  // Note: using useRef instead of useState to have access to these variables inside PinchZoom class
  const reachedEdgeRef = React.useRef<'left' | 'right' | null>(null)
  const touchAreaRef = React.useRef<ZoomTouchArea>(null)
  const isZoomedRef = React.useRef(isZoomed)
  const lastZoomEventTime = React.useRef(0)
  React.useEffect(() => {
    isZoomedRef.current = isZoomed
  }, [isZoomed])

  // Note: zoom out of the privious image after the user has moved to the next one
  const resetZoom = () => {
    pinchZoomNodes.forEach(item => {
      item?.zoomOutAnimation()
    })
  }

  const loadHighResolutionImage = (
    node: HTMLImageElement,
    params: ExtendedPinchZoom,
    zoomedImageSource: string
  ) => {
    /**
     * Note: replacing src of PinchZoom image causes its offset to be reset
     * Therefore, we need to set offset to its original value
     */
    const { x, y } = params.offset
    node.onload = () => {
      params.addOffset({ x, y })
    }

    node.src = zoomedImageSource
  }

  const pinchZoomImageRefCallback = React.useCallback(
    (
      node: HTMLImageElement | null,
      index: number,
      imageSource: string,
      zoomedImageSource: string
    ) => {
      if (node !== null && !pinchZoomNodes?.[index] && isImageZoomShown) {
        const pinchZoom = new PinchZoom(node, {
          onZoomUpdate(params) {
            const extendedParams = params as ExtendedPinchZoom

            if (extendedParams.zoomFactor !== 1) {
              const zoomedImageHasNotLoaded =
                node.src !== zoomedImageSource && zoomedImageSource !== imageSource

              /**
               * Call loadHighResolutionImage after the user has finished zooming in
               * i.e. time difference between Date.now() and the time when the last zoom event happened is >= 300ms
               */
              if (zoomedImageHasNotLoaded) {
                lastZoomEventTime.current = Date.now()
                setTimeout(() => {
                  const timePassed = Date.now() - lastZoomEventTime.current
                  if (timePassed >= maxIntervalBetweenZoomEvents) {
                    loadHighResolutionImage(node, extendedParams, zoomedImageSource)
                  }
                }, maxIntervalBetweenZoomEvents)
              }
              setIsZoomed(true)
            } else {
              setIsZoomed(false)
            }
          },

          onDragStart(_, event) {
            const clientX = event?.touches?.[0]?.clientX
            if (clientX) {
              if (clientX <= edgeSwipeThreshold) {
                touchAreaRef.current = 'left'
              } else if (
                clientX >=
                document.body.getBoundingClientRect().width - edgeSwipeThreshold
              ) {
                touchAreaRef.current = 'right'
              } else {
                touchAreaRef.current = 'middle'
              }
            }
          },

          /**
           * When zoomed, capture drag events for PinchZoom panning and to prevent native zoom.
           * When not zoomed, allow the carousel to slide.
           * Also allow the carousel to slide when panned to a side and touch starts on that side.
           */
          onDragUpdate: (_params, event) => {
            const isZoomed = isZoomedRef.current
            const swipeIsFromReachedEdge = touchAreaRef.current === reachedEdgeRef.current
            const stopCarouselDrag = isZoomed && !swipeIsFromReachedEdge // pan zoomed image
            const preventNativeZoom = event.touches.length > 1

            if (stopCarouselDrag || preventNativeZoom) event.stopPropagation()
          },

          onDragEnd(params) {
            const extendedParams = params as ExtendedPinchZoom
            const reachedLeftEdge = extendedParams.offset.x <= edgeTouchThreshold
            const reachedRightEdge =
              extendedParams.offset.x >=
              document.body.getBoundingClientRect().width - edgeTouchThreshold

            if (extendedParams.zoomFactor !== 1 && (reachedLeftEdge || reachedRightEdge)) {
              if (!isMouseDown) {
                if (reachedLeftEdge) {
                  reachedEdgeRef.current = 'left'
                } else if (reachedRightEdge) {
                  reachedEdgeRef.current = 'right'
                }
              }
            } else {
              reachedEdgeRef.current = null
            }
          },
        })

        setPinchZoomNode(index, pinchZoom as ExtendedPinchZoom)
      }
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideImageZoom, pinchZoomNodes, isImageZoomShown]
  )

  const renderProductAsset = React.useCallback(
    (asset: ProductAsset, index: number): JSX.Element | null => {
      switch (asset.type) {
        case 'ProductImage':
          const imageSource = resizeShopifyImageSource({
            height: imageSize,
            source: asset.source,
            width: imageSize,
          })
          const zoomedImageSource = resizeShopifyImageSource({
            height: zoomedImageSize,
            source: asset.source,
            width: zoomedImageSize,
          })

          return (
            <PinchZoomImageWrapper>
              <PinchZoomImage
                alt={`${index + 1} of ${allAssets.length}`}
                ref={instance =>
                  pinchZoomImageRefCallback(instance, index, imageSource, zoomedImageSource)
                }
                src={asset.source}
                widths={{ unit: 'px', sm: imageSize, md: imageSize }}
                aspectRatios={{ sm: null, md: null }}
                loading='default'
                onMouseDown={_ => setIsMouseDown(true)}
                onMouseUp={_ => setIsMouseDown(false)}
              />
            </PinchZoomImageWrapper>
          )
        case 'ProductVideo':
          return (
            <ZoomedProductVideo displayMuteButton={asset.kind === 'ugc'} source={asset.source} />
          )
        default:
          return null
      }
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinchZoomImageRefCallback]
  )

  React.useEffect(() => {
    resetZoom()
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssetIndex])

  return (
    <NextModal
      bodyOpenClassName={standardModalBodyOpenClassName}
      closeTimeoutMS={modalAnimationDuration}
      isOpen={isImageZoomShown}
      onRequestClose={hideImageZoom}
      portalClassName={imageZoomPortalClassName}
      style={modalStyles}
    >
      <Container>
        <CloseButton
          onClick={hideImageZoom}
          aria-label={strings.closeZoom}
          {...trackEvent({ category: 'gallery', action: 'close' })}
        >
          <SelectMark height='16px' width='16px' stroke={isSmallViewport ? 'white' : undefined} />
        </CloseButton>

        <LeftArrowWrap
          onClick={revealPreviousSlide}
          aria-label={strings.previousImage}
          {...trackEvent({ category: 'gallery', action: 'view previous image' })}
        >
          {isSmallViewport ? (
            <CaretLeft />
          ) : (
            <RightArrow height='24px' width='28px' rotateClockwise={RotateClockwise.Half} />
          )}
        </LeftArrowWrap>
        <RightArrowWrap
          onClick={revealNextSlide}
          aria-label={strings.nextImage}
          {...trackEvent({ category: 'gallery', action: 'view next image' })}
        >
          {isSmallViewport ? <CaretRight /> : <RightArrow height='24px' width='28px' />}
        </RightArrowWrap>

        <Media lessThan='md'>
          <GalleryImageCarousel {...{ renderProductAsset }} isFullScreen={true} />
          <GalleryImagePaginationText />
        </Media>

        <Media greaterThanOrEqual='md'>
          <GalleryImageCarousel isFullScreen={true} scrollableSlides />
        </Media>
      </Container>
    </NextModal>
  )
}

const ZoomedProductVideo: React.FC<{ source: string; displayMuteButton?: boolean }> = ({
  source,
  displayMuteButton,
}) => {
  const {
    magnolia: {
      general: { playVideo, pauseVideo, muteVideo, unmuteVideo },
    },
  } = useTranslation()
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isPaused, setIsPaused] = React.useState<boolean>(false)
  const [isMuted, setIsMuted] = React.useState<boolean>(true)
  const toggleVideoPause = () => {
    if (isPaused) {
      videoRef.current?.play()
      setIsPaused(false)
    } else {
      videoRef.current?.pause()
      setIsPaused(true)
    }
  }
  const toggleVideoMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
    }
    setIsMuted(!isMuted)
  }
  return (
    <>
      <Video autoPlay muted playsInline loop ref={videoRef}>
        <source src={source} type='video/mp4' />
      </Video>
      <StyledVideoButtonsWrapper>
        {
          <PauseButton
            onClick={() => {
              toggleVideoPause()
            }}
            pauseText={pauseVideo}
            playText={playVideo}
            paused={isPaused}
          />
        }
        {displayMuteButton && (
          <MuteButton
            onClick={() => {
              toggleVideoMute()
            }}
            isMuted={isMuted}
            mutedText={muteVideo}
            notMutedText={unmuteVideo}
          />
        )}
      </StyledVideoButtonsWrapper>
    </>
  )
}
